import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { ComponentErrorBoundary } from './components/ComponentErrorBoundary';
import { useCANvisionContext } from './context/useCANvisionContext';
import { useCameraSets } from './hooks/useCameraSets';
import CameraCell from './components/CameraGrid/CameraCell';
import {
  FullscreenContainer,
  VideoWallControls,
  useVideoWallRotation,
  useVideoWallAlerts,
} from './components/VideoWall';
import type { VMSCamera, CameraSet } from './types/canvision.types';

// ---------------------------------------------------------------------------
// Local-storage helpers
// ---------------------------------------------------------------------------

function readLocalStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function useLocalStorage<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => readLocalStorage(key, fallback));

  const setValue = useCallback(
    (value: T) => {
      setState(value);
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Quota exceeded or other storage error — ignore silently.
      }
    },
    [key],
  );

  return [state, setValue];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PER_PAGE_OPTIONS = [4, 8, 12, 16] as const;
const COLUMN_OPTIONS = [3, 4] as const;

// ---------------------------------------------------------------------------
// VideoWallControlBar — top control bar for the page
// ---------------------------------------------------------------------------

interface VideoWallControlBarProps {
  // Camera set selector
  sets: CameraSet[];
  selectedSetId: number | null;
  onSetChange: (setId: number | null) => void;
  setsLoading: boolean;
  // Pagination
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  // Per-page
  camerasPerPage: number;
  onCamerasPerPageChange: (perPage: number) => void;
  // Layout columns
  columnsPerRow: number;
  onColumnsPerRowChange: (cols: number) => void;
  // Rotation controls
  rotation: ReturnType<typeof useVideoWallRotation>;
  dwellTime: number;
  onDwellTimeChange: (seconds: number) => void;
  // Alert props
  alertCount?: number;
  onToggleAlerts?: () => void;
  // Fullscreen — injected by FullscreenContainer via cloneElement
  isFullscreen?: boolean;
  isFullscreenSupported?: boolean;
  onToggleFullscreen?: () => void;
}

function VideoWallControlBar({
  sets,
  selectedSetId,
  onSetChange,
  setsLoading,
  page,
  pageCount,
  onPageChange,
  camerasPerPage,
  onCamerasPerPageChange,
  columnsPerRow,
  onColumnsPerRowChange,
  rotation,
  dwellTime,
  onDwellTimeChange,
  alertCount,
  onToggleAlerts,
  isFullscreen,
  isFullscreenSupported,
  onToggleFullscreen,
}: VideoWallControlBarProps) {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        {/* ---- Left: VideoWallControls (set selector + rotation + dwell + fullscreen + alerts) ---- */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <VideoWallControls
            sets={sets}
            selectedSetId={selectedSetId}
            onSetChange={onSetChange}
            dwellTime={dwellTime}
            onDwellTimeChange={onDwellTimeChange}
            isPlaying={rotation.isPlaying}
            onPlay={rotation.play}
            onPause={rotation.pause}
            onNext={rotation.next}
            onPrevious={rotation.previous}
            onGoToSet={rotation.goToSet}
            currentSetIndex={rotation.currentSetIndex}
            currentSet={rotation.currentSet}
            timeRemaining={rotation.timeRemaining}
            disabled={setsLoading}
            isFullscreen={isFullscreen}
            isFullscreenSupported={isFullscreenSupported}
            onToggleFullscreen={onToggleFullscreen}
            alertCount={alertCount}
            onToggleAlerts={onToggleAlerts}
          />
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* ---- Center: Pagination ---- */}
        {pageCount > 1 && (
          <>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_e, value) => onPageChange(value)}
              size="small"
              color="primary"
            />
            <Divider orientation="vertical" flexItem />
          </>
        )}

        {/* ---- Right: Per-page select + column toggle ---- */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {/* Per page selector */}
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel id="vw-per-page-label">Per Page</InputLabel>
            <Select
              labelId="vw-per-page-label"
              value={camerasPerPage}
              onChange={(e: SelectChangeEvent<number>) =>
                onCamerasPerPageChange(e.target.value as number)
              }
              label="Per Page"
            >
              {PER_PAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Column layout toggle */}
          <ToggleButtonGroup
            size="small"
            value={columnsPerRow}
            exclusive
            onChange={(_e, value) => {
              if (value !== null) onColumnsPerRowChange(value as number);
            }}
            aria-label="cameras per row"
          >
            {COLUMN_OPTIONS.map((cols) => (
              <ToggleButton key={cols} value={cols} aria-label={`${cols} columns`}>
                <Tooltip title={`${cols} per row`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GridViewIcon fontSize="small" />
                    <Typography variant="caption">{cols}</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// CANvisionVideoWall — main page component
// ---------------------------------------------------------------------------

export function CANvisionVideoWall() {
  const { cameras, camerasLoading, selectedSiteId } = useCANvisionContext();
  const { sets: cameraSets, loading: setsLoading } = useCameraSets();

  // Persisted layout preferences
  const [columnsPerRowRaw, setColumnsPerRow] = useLocalStorage<number>('canvision:vw-columns', 4);
  // Clamp to valid range (3 or 4) — handles legacy "2" values from localStorage
  const columnsPerRow = columnsPerRowRaw < 3 ? 3 : columnsPerRowRaw;
  const [camerasPerPage, setCamerasPerPage] = useLocalStorage<number>('canvision:vw-per-page', 8);
  const [selectedSetId, setSelectedSetId] = useLocalStorage<number | null>('canvision:vw-set-id', null);
  const [dwellTime, setDwellTime] = useLocalStorage<number>('canvision:vw-dwell', 15);

  // Page state (reset when source data changes)
  const [page, setPage] = useState(1);

  // Alerts
  const {
    alerts,
    dismissAlert,
    markViewed,
    unviewedCount,
  } = useVideoWallAlerts({ enableMockData: false, enableAutoDismiss: true });
  const [alertsVisible, setAlertsVisible] = useState(true);

  // Rotation
  const rotation = useVideoWallRotation({
    sets: cameraSets,
    dwellTimeSeconds: dwellTime,
    enabled: selectedSetId !== null && cameraSets.length > 1,
  });

  // ---------------------------------------------------------------------------
  // Derive the active camera list based on set selection
  // ---------------------------------------------------------------------------
  const activeCameras: VMSCamera[] = useMemo(() => {
    if (selectedSetId === null) {
      // "All Cameras" mode — show all cameras from context
      return cameras;
    }

    const selectedSet = cameraSets.find((s) => s.id === selectedSetId);
    if (!selectedSet) return cameras;

    // Map CameraSetCamera entries to matching VMSCamera objects (by camera_id)
    const cameraIdSet = new Set(selectedSet.cameras.map((c) => c.camera_id));
    return cameras.filter((cam) => cameraIdSet.has(cam.id));
  }, [cameras, cameraSets, selectedSetId]);

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const pageCount = Math.max(1, Math.ceil(activeCameras.length / camerasPerPage));

  // Clamp page when data changes
  const safePage = Math.min(page, pageCount);
  if (safePage !== page) {
    // Will re-render with clamped page
    setPage(safePage);
  }

  const pagedCameras = activeCameras.slice(
    (safePage - 1) * camerasPerPage,
    safePage * camerasPerPage,
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleCamerasPerPageChange = useCallback(
    (perPage: number) => {
      setCamerasPerPage(perPage);
      setPage(1); // Reset to first page
    },
    [setCamerasPerPage],
  );

  const handleSetChange = useCallback(
    (setId: number | null) => {
      setSelectedSetId(setId);
      setPage(1); // Reset to first page when switching sets
    },
    [setSelectedSetId],
  );

  const handleCameraFullscreen = useCallback((_camera: VMSCamera) => {
    // Camera fullscreen handled by CameraCell's own fullscreen button.
    // In the future this can open a CameraDetail modal via context.
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        p: { xs: 1, sm: 0 },
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {/* Control bar + Camera grid wrapped in FullscreenContainer */}
      <FullscreenContainer
        isPlaying={rotation.isPlaying}
        controls={
          <VideoWallControlBar
            sets={cameraSets}
            selectedSetId={selectedSetId}
            onSetChange={handleSetChange}
            setsLoading={setsLoading}
            page={safePage}
            pageCount={pageCount}
            onPageChange={setPage}
            camerasPerPage={camerasPerPage}
            onCamerasPerPageChange={handleCamerasPerPageChange}
            columnsPerRow={columnsPerRow}
            onColumnsPerRowChange={setColumnsPerRow}
            rotation={rotation}
            dwellTime={dwellTime}
            onDwellTimeChange={setDwellTime}
            alertCount={unviewedCount}
            onToggleAlerts={() => setAlertsVisible((v) => !v)}
          />
        }
        alerts={alerts}
        onViewAlert={(alert) => markViewed(alert.id)}
        onDismissAlert={(alertId) => dismissAlert(alertId)}
        alertsVisible={alertsVisible}
      >
        <ComponentErrorBoundary sectionName="Video Wall">
          {/* Loading state */}
          {camerasLoading || setsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography color="text.secondary">Loading cameras...</Typography>
            </Box>
          ) : pagedCameras.length === 0 ? (
            /* Empty state */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
                gap: 2,
              }}
            >
              <VideocamOffIcon sx={{ fontSize: 48, color: 'grey.500' }} />
              <Typography color="text.secondary" variant="h6">
                No cameras to display
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {selectedSetId !== null
                  ? 'The selected camera set has no cameras. Choose a different set or select "All Cameras".'
                  : 'No cameras are available. Make sure a site is selected and cameras have been synced.'}
              </Typography>
            </Box>
          ) : (
            /* Camera grid */
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: `repeat(${Math.min(columnsPerRow, 2)}, 1fr)`,
                  md: `repeat(${columnsPerRow}, 1fr)`,
                },
                gap: 1,
              }}
            >
              {pagedCameras.map((cam) => (
                <CameraCell
                  key={cam.id}
                  camera={cam}
                  siteId={cam.site_id ?? selectedSiteId}
                  onFullscreen={handleCameraFullscreen}
                />
              ))}
            </Box>
          )}
        </ComponentErrorBoundary>
      </FullscreenContainer>
    </Box>
  );
}

export default CANvisionVideoWall;
